import { useMutation } from '@apollo/client';
import { DeleteImpactDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import { useDeleteImpactTRPC } from './useDeleteImpactTRPC';

export const useDeleteImpact = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  // GraphQL mutation
  const [deleteImpactGraphQL, graphqlState] = useMutation(
    DeleteImpactDocument,
    {
      update: (cache) => {
        evictField(cache, 'impact');
        evictField(cache, 'impact_rating');
        evictField(cache, 'impact_aggregate');
        evictField(cache, 'impact_rating_aggregate');
      },
    }
  );

  // tRPC mutation
  const trpcMutation = useDeleteImpactTRPC();

  const deleteImpact = async (variables: { Id: string }): Promise<void> => {
    if (trpcEnabled) {
      return trpcMutation.deleteImpact(variables);
    }

    const result = await deleteImpactGraphQL({
      variables: {
        Id: variables.Id,
      },
    });

    if (!result.data) {
      throw new Error('Failed to delete impact');
    }
  };

  // Return appropriate state based on feature flag
  if (trpcEnabled) {
    return {
      deleteImpact,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    deleteImpact,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
